import { Select } from '@components/select';
import { TextField } from '@components/textField';
import { NAMED_INSURED } from '@constants/namedInsured';
import { businessTypeItems, Customer, BusinessType, AddressValidationStatus } from '@models/customer';
import { AddressInput, EnteredAddress } from '@models/validation';
import { Box, Grid, InputAdornment, Typography, CircularProgress, Button } from '@mui/material';
import { MessageBrokerageAssigned } from '@pages/NewQuote/namedInsured/MessageBrokerageAssigned';
import { CustomerMatchModal } from '@pages/NewQuote/namedInsured/CustomerMatchModal';
import { PanelBase, PanelBaseType } from '@pages/NewQuote/PanelBase/PanelBase';
import { boxStyles } from '@pages/NewQuote/scope/ScopePanel.styles';
import { ITogglePanel } from '@pages/NewQuote/TogglePanel';
import { CustomerMatchCandidate, useMatchCustomer, customerQueryKey } from '@services/customer';
import { useCustomerContactInfo } from '@services/customer';
import { useStore } from '@store/GlobalStore';
import { useNewQuoteStore } from '@store/NewQuote';
import { KeyValueOptions } from '@utils/commonTypes';
import { useMemo, useState, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { NoMatchingAddress } from '@pages/NewQuote/location/NoMatchingAddress';
import { useValidPhysicalAddress } from '@services/validation';
import { BusinessAddress as BusinessAddressType } from '@models/customer';
import { BusinessAddress as BusinessAddressComponent } from '@pages/NewQuote/namedInsured/BusinessAddress';
import { convertUnicodeToAscii, isInvalidAscii } from '@utils/convertUnicodeToAscii';
import { ProductOfferingId } from '@models/newQuote';
import { CustomerMatchResponse, ContactInfoGetResponse } from '@lio/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

type NewCustomerField = Record<string, KeyValueOptions>;

interface INamedInsuredOpenPanel extends ITogglePanel {
  onFieldChange: (value: boolean) => void;
}

const initialAddress: AddressInput = {
  street1: '',
  street2: '',
  state: '',
  city: '',
  zipCode: '',
};

export const NamedInsuredOpenPanel = ({ onClose, onFieldChange }: INamedInsuredOpenPanel) => {
  const { isCustomerUser, isInternalUser } = useStore((state) => state.userAuth, shallow);
  const [isValidCustomer, setIsValidCustomer] = useState(false);
  const { scope, namedInsured, updateNamedInsurance } = useNewQuoteStore();
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const { customer } = namedInsured;
  const { isSpecialEvent } = scope;
  const anchorRef = useRef(null);

  const [formState, setFormState] = useState({
    inputValue: customer?.name || '',
    businessAddress: customer?.businessAddress || undefined,
    selectedValue: customer || null,
    selectedCustomerId: undefined as string | undefined,
    isFormCollapsed: false,
    needsBusinessType: true,
    isExistingCustomer: false,
    hasNoMatch: false,
    hasBrokerageAssigned: false,
    namedInsuredErrorMessage: undefined as string | undefined,
    isReadOnly: false,
    isVerifyingAddress: false,
  });

  const [modalState, setModalState] = useState({
    isMatchModalOpen: false,
    matchResult: undefined as CustomerMatchResponse | undefined,
  });

  const [newCustomer, setNewCustomer] = useState<NewCustomerField>({
    businessType: {
      key: 'businessType',
      value: customer?.businessType || '',
      label: isSpecialEvent ? 'Applicant Type' : 'Business Type',
    },
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showNoMatchingAddress, setShowNoMatchingAddress] = useState(false);

  const { contactInfo } = useCustomerContactInfo(formState.selectedValue?.id ?? '');
  const { matchCustomerAsync, isMatchingCustomer } = useMatchCustomer();
  const queryClient = useQueryClient();
  const client = useClient();

  const { validPhysicalAddress, refetchAddress } = useValidPhysicalAddress(formState.businessAddress || initialAddress);

  const createAddressObject = (address: EnteredAddress): BusinessAddressType => ({
    street1: address.street1 || '',
    street2: address.street2 || '',
    city: address.city || '',
    state: address.state || '',
    zipCode: address.zipCode || '',
  });

  const getCurrentBusinessType = (): BusinessType | undefined => {
    const businessTypeValue =
      newCustomer.businessType.value || formState.selectedValue?.businessType || customer?.businessType;
    return businessTypeValue as BusinessType | undefined;
  };

  const updateFormStateWithAddress = (address: BusinessAddressType, isReadOnly = false) => {
    const currentBusinessType = getCurrentBusinessType();

    setFormState((prev) => ({
      ...prev,
      isReadOnly,
      businessAddress: address,
      selectedValue: prev.selectedValue
        ? {
            ...prev.selectedValue,
            businessType: currentBusinessType as BusinessType,
          }
        : null,
    }));

    updateNamedInsurance({
      ...(formState.selectedValue as Customer),
      name: formState.inputValue,
      businessAddress: address,
      addresses: contactInfo?.addresses,
      businessType: currentBusinessType as BusinessType,
    });
  };

  const resetFormToInitialState = () => {
    setFormState({
      inputValue: '',
      businessAddress: undefined,
      selectedValue: null,
      selectedCustomerId: undefined,
      isFormCollapsed: false,
      needsBusinessType: true,
      isExistingCustomer: false,
      hasNoMatch: false,
      hasBrokerageAssigned: false,
      namedInsuredErrorMessage: undefined,
      isReadOnly: false,
      isVerifyingAddress: false,
    });

    setNewCustomer({
      businessType: {
        key: 'businessType',
        value: '',
        label: isSpecialEvent ? 'Applicant Type' : 'Business Type',
      },
    });
    setIsValidCustomer(false);
  };

  const resetFormToCustomerState = () => {
    setFormState((prev) => ({
      ...prev,
      businessAddress: customer?.businessAddress as BusinessAddressType | undefined,
      selectedValue: customer || null,
      selectedCustomerId: undefined,
      isFormCollapsed: false,
      needsBusinessType: true,
      isExistingCustomer: false,
      hasNoMatch: false,
      hasBrokerageAssigned: false,
    }));

    setNewCustomer((prev) => ({
      businessType: {
        key: 'businessType',
        value: prev.businessType.value || customer?.businessType || '',
        label: isSpecialEvent ? 'Applicant Type' : 'Business Type',
      },
    }));

    setModalState({
      isMatchModalOpen: false,
      matchResult: undefined,
    });
    setIsValidCustomer(false);
  };

  const closeAddressPopover = () => {
    setShowNoMatchingAddress(false);
    setAnchorEl(null);
  };

  const handleProceedAsEntered = () => {
    const customerToCreate: Customer = {
      ...(formState.selectedValue as Customer),
      id: '',
      name: formState.inputValue.trim(),
      businessAddress: formState.businessAddress as BusinessAddressType,
      businessType: getCurrentBusinessType() || ('Corporation' as BusinessType),
      createAsEntered: true,
    };

    setFormState((prev) => ({
      ...prev,
      selectedCustomerId: undefined,
      needsBusinessType: true,
      isExistingCustomer: false,
      selectedValue: customerToCreate,
      hasNoMatch: true,
      isFormCollapsed: false,
      isReadOnly: true,
    }));

    updateNamedInsurance(customerToCreate);
    setModalState((prev) => ({ ...prev, isMatchModalOpen: false }));
    setIsValidCustomer(true);
  };

  const proceedWithNoMatch = () => {
    updateNamedInsurance({
      ...(formState.selectedValue as Customer),
      name: formState.inputValue,
      businessAddress: formState.businessAddress as BusinessAddressType,
      addresses: contactInfo?.addresses,
      id: formState.selectedCustomerId || '',
      businessType: newCustomer.businessType.value as BusinessType,
    });
    setFormState((prev) => ({ ...prev, isReadOnly: true }));
    setIsValidCustomer(true);
  };

  const performCustomerMatch = async () => {
    try {
      const result = await matchCustomerAsync({
        name: formState.inputValue.trim(),
        address: {
          street1: formState.businessAddress?.street1 || '',
          street2: formState.businessAddress?.street2 || undefined,
          city: formState.businessAddress?.city || '',
          state: formState.businessAddress?.state || '',
          zipCode: formState.businessAddress?.zipCode || '',
        },
      });

      if (result.outcome === 'no-match') {
        proceedWithNoMatch();
      } else {
        setModalState({ isMatchModalOpen: true, matchResult: result });
      }
    } catch (error) {
      // Matching is an assist, not a gate — if the check fails, let the user continue with what they entered.
      proceedWithNoMatch();
    }
  };

  const handleCandidateSelection = async (candidate: CustomerMatchCandidate) => {
    const completeBusinessAddress: BusinessAddressType = {
      street1: candidate.address.street1,
      street2: candidate.address.street2 || '',
      city: candidate.address.city,
      state: candidate.address.state,
      zipCode: candidate.address.zipCode,
    };

    // The candidate is an existing local customer, so load its real contact info here rather
    // than reusing the previously selected customer's — otherwise the create-customer flow can
    // interpret a missing business address as absent and overwrite the candidate's addresses.
    const candidateContactInfo = await queryClient.fetchQuery({
      queryKey: customerQueryKey.searchContactInfo({ customerId: candidate.id }),
      queryFn: () => client.get(`customer/${candidate.id}/contact-info`).json<ContactInfoGetResponse>(),
    });

    const completeCustomer: Customer = {
      ...(customer as Customer),
      id: candidate.id,
      name: candidate.name,
      businessType: customer?.businessType || ('Corporation' as BusinessType),
      businessAddress: completeBusinessAddress,
      addresses: candidateContactInfo.addresses,
      jurisdiction: {
        id: '',
        abbreviation: candidate.address.state,
      },
    };

    setFormState((prev) => ({
      ...prev,
      selectedCustomerId: candidate.id,
      inputValue: candidate.name,
      businessAddress: completeBusinessAddress,
      selectedValue: completeCustomer,
      isExistingCustomer: true,
      needsBusinessType: true,
      isFormCollapsed: false,
      isReadOnly: true,
    }));

    updateNamedInsurance(completeCustomer);
    setModalState((prev) => ({ ...prev, isMatchModalOpen: false }));
    onFieldChange(true);
    setIsValidCustomer(true);
  };

  const handleBusinessTypeSelection = (businessType: string) => {
    setNewCustomer((prev) => ({
      ...prev,
      businessType: { ...prev.businessType, value: businessType },
    }));

    if (businessType && formState.selectedValue) {
      setFormState((prev) => ({
        ...prev,
        selectedValue: {
          ...prev.selectedValue,
          businessType: businessType as BusinessType,
        } as Customer,
      }));
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const originalValue = event.target.value;

    const value = convertUnicodeToAscii(originalValue);
    const invalidAsciiMessage = isInvalidAscii(value);

    if (invalidAsciiMessage) {
      setFormState((prev) => ({
        ...prev,
        inputValue: value,
        namedInsuredErrorMessage: invalidAsciiMessage,
      }));
      return;
    }

    if (value.length > NAMED_INSURED.MAX_LENGTH) {
      setFormState((prev) => ({
        ...prev,
        namedInsuredErrorMessage: `Named Insured must be less than ${NAMED_INSURED.MAX_LENGTH} characters.`,
      }));
      return;
    }

    setFormState((prev) => ({
      ...prev,
      inputValue: value,
      namedInsuredErrorMessage: undefined,
    }));
  };

  const handleInputBlur = () => {
    if (formState.inputValue.trim() !== customer?.name?.trim()) {
      resetFormToCustomerState();
    } else {
      // trim input before continuing
      setFormState((prev) => ({
        ...prev,
        inputValue: formState.inputValue.trim(),
      }));
    }
  };

  const handleAddressChange = (addressToUpdate?: AddressInput) => {
    if (addressToUpdate) {
      setFormState((prev) => ({ ...prev, businessAddress: addressToUpdate as BusinessAddressType }));
    }
  };

  const verifyAddress = async () => {
    setFormState((prev) => ({ ...prev, isVerifyingAddress: true }));

    try {
      const response = await refetchAddress();
      if (response.isSuccess && response.data.status.includes(AddressValidationStatus.ExactMatch)) {
        setFormState((prev) => ({ ...prev, isVerifyingAddress: false }));
        await performCustomerMatch();
      } else {
        setAnchorEl(anchorRef.current);
        setShowNoMatchingAddress(true);
        setFormState((prev) => ({ ...prev, isVerifyingAddress: false }));
      }
    } catch (error) {
      setAnchorEl(anchorRef.current);
      setShowNoMatchingAddress(true);
      setFormState((prev) => ({ ...prev, isVerifyingAddress: false }));
    }
  };

  const handleProceedUsingThisName = async () => {
    if (formState.businessAddress) {
      await verifyAddress();
    }
  };

  const handleConfirm = () => {
    if (formState.selectedValue) {
      setFormState((prev) => ({ ...prev, isVerifyingAddress: false }));
    }
    onClose();
  };

  const handleChooseNewCustomer = () => {
    resetFormToInitialState();
  };

  const isCondoAssociationSelected = scope.productOffering?.value === ProductOfferingId.CondoOwnersAssociation;
  const isCAAddress = (formState.businessAddress?.state || '').toUpperCase() === 'CA';
  const isRestrictedExternalCondoCA = !isInternalUser && isCondoAssociationSelected && isCAAddress;

  const canContinue = useMemo(() => {
    if (formState.isReadOnly) return true;

    return !!(
      formState.inputValue.trim() &&
      formState.businessAddress?.street1 &&
      formState.businessAddress?.city &&
      formState.businessAddress?.state &&
      formState.businessAddress?.zipCode &&
      !formState.namedInsuredErrorMessage &&
      newCustomer.businessType.value &&
      !isRestrictedExternalCondoCA
    );
  }, [
    formState.inputValue,
    formState.businessAddress,
    formState.namedInsuredErrorMessage,
    formState.isReadOnly,
    newCustomer.businessType.value,
    isRestrictedExternalCondoCA,
  ]);

  const mainAction = {
    label: 'Continue to Location',
    disabled: !canContinue || !isValidCustomer || isRestrictedExternalCondoCA,
    onClick: handleConfirm,
  };

  return (
    <PanelBase
      name={isCustomerUser ? 'Customer' : 'Named Insured'}
      step='2 of 4'
      type={PanelBaseType.Open}
      confirmButtonProps={{
        children: mainAction.label,
        disabled: mainAction.disabled,
        onClick: mainAction.onClick,
        isLoading: isMatchingCustomer || formState.isVerifyingAddress,
      }}
    >
      <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }} size={{ xs: 12 }}>
        <Typography variant='h4'>Who is being insured?</Typography>

        {formState.isFormCollapsed ? (
          <Box sx={{ ...boxStyles, p: 2, borderRadius: 1, mb: 2, gap: 2 }}>
            <Typography variant='bodySmall'>{formState.inputValue}</Typography>
            <Typography variant='bodySmall'>
              {formState.businessAddress?.street1}, {formState.businessAddress?.city},{' '}
              {formState.businessAddress?.state} {formState.businessAddress?.zipCode}
            </Typography>
            {!formState.isReadOnly && (
              <Button onClick={handleChooseNewCustomer} variant='outlined' fullWidth>
                Choose New Customer
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Box sx={boxStyles}>
              <Grid size={{ xs: 12 }} sx={{ width: isMobileOrSmallScreen ? '100%' : '500px' }}>
                <TextField
                  fullWidth={!isMobileOrSmallScreen}
                  sx={{ width: isMobileOrSmallScreen ? '100%' : '500px' }}
                  inputLabel={isCustomerUser ? 'Name of Business or Organization to be Insured' : 'Named Insured'}
                  id='NamedInsuredField'
                  value={formState.inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  error={!!formState.namedInsuredErrorMessage}
                  helperLabel={formState.namedInsuredErrorMessage}
                  disabled={formState.isReadOnly}
                  autoComplete='off'
                  slotProps={{
                    inputLabel: { htmlFor: 'NamedInsuredField' },
                    input: {
                      endAdornment:
                        isMatchingCustomer || formState.isVerifyingAddress ? (
                          <InputAdornment
                            position='end'
                            disablePointerEvents
                            sx={{ position: 'absolute', right: 8, color: 'primary.main' }}
                          >
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : undefined,
                    },
                  }}
                />
              </Grid>
            </Box>

            <Grid size={{ xs: 12 }} sx={{ width: isMobileOrSmallScreen ? '100%' : '500px' }}>
              <Select
                fullWidth
                inputLabel={newCustomer.businessType.label}
                value={newCustomer.businessType.value}
                onChangeSelect={handleBusinessTypeSelection}
                items={businessTypeItems.sort((a, b) => a.label.localeCompare(b.label))}
                sx={{ width: isMobileOrSmallScreen ? '100%' : '500px' }}
                disabled={formState.isReadOnly}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ width: isMobileOrSmallScreen ? '100%' : '500px' }}>
              <BusinessAddressComponent
                handleConfirmNewCustomer={(address) =>
                  setFormState((prev) => ({ ...prev, businessAddress: address as BusinessAddressType }))
                }
                isNewCustomer={false}
                address={formState.businessAddress || initialAddress}
                updateAddress={handleAddressChange}
                isBusinessTypeMissing={false}
                disabled={formState.isReadOnly}
                isSpecialEvent={isSpecialEvent}
                skipValidation={formState.isExistingCustomer}
              />
            </Grid>

            {isRestrictedExternalCondoCA && (
              <Typography
                variant='bodySmall'
                fontWeight='bold'
                sx={{ mt: 2, color: 'error.main', textAlign: 'center' }}
              >
                Condominium Association policies are not available in California.
                <br />
                Please contact support at 1-877-LIO-7155 (1-877-546-7155) for more information.
              </Typography>
            )}

            {!formState.isReadOnly ? (
              <>
                <Typography variant='body2' sx={{ mt: 2, textAlign: 'center' }}>
                  If the information entered is correct, you can Proceed Using This Name
                </Typography>
                <Button
                  variant='text'
                  onClick={handleProceedUsingThisName}
                  disabled={!canContinue || isMatchingCustomer || isRestrictedExternalCondoCA}
                  sx={{ mt: 2 }}
                >
                  Proceed Using This Name
                </Button>
              </>
            ) : (
              <Button variant='text' onClick={handleChooseNewCustomer} sx={{ mt: 2 }}>
                Choose New Customer
              </Button>
            )}
          </>
        )}

        <Grid container ref={anchorRef} sx={{ justifyContent: 'center', width: '100%', pt: 3 }}></Grid>
        {formState.hasBrokerageAssigned && <MessageBrokerageAssigned />}
      </Grid>

      <CustomerMatchModal
        open={modalState.isMatchModalOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isMatchModalOpen: false }))}
        matchResult={modalState.matchResult}
        onSelectCandidate={handleCandidateSelection}
        onProceedAsEntered={handleProceedAsEntered}
        enteredName={formState.inputValue}
        enteredAddress={formState.businessAddress}
        cancelButtonText='Exit'
        proceedButtonText='Use Selected Customer'
      />

      <NoMatchingAddress
        id='no-matching-address-popover'
        aria-labelledby='use-this-address-button'
        addressOptions={validPhysicalAddress?.items}
        open={showNoMatchingAddress}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 400,
          horizontal: 'center',
        }}
        enteredAddress={formState.businessAddress || initialAddress}
        onAddressConfirm={(address) => {
          if (!address) return;

          closeAddressPopover();
          const updatedAddress = createAddressObject(address);
          updateFormStateWithAddress(updatedAddress, true);
          setIsValidCustomer(true);
        }}
        onPopoverClose={closeAddressPopover}
        onSelectAddress={(address) => {
          if (!address) return;

          const updatedAddress = createAddressObject(address);
          updateFormStateWithAddress(updatedAddress);
        }}
        onInvalidAdress={closeAddressPopover}
      />
    </PanelBase>
  );
};
